// Copyright (c) 2022 by Move Closer

import { RequestResetLinkFormData } from '../organisms/RequestResetLinkForm'

import { TokenModel } from './models'

export const AuthControlServiceType = Symbol.for('IAuthControl')

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export enum SocialAuthType {
  Facebook = 'facebook',
  Google = 'google'
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export type SocialAuthRedirectUri = string

/**
 * Params provided by SSO provider after successful social OAuth
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface SSOCallbackParams {
  authuser: string
  code: string
  hd?: string
  prompt: string
  scope: string
  state: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface SSOValidCallbackResponse {
  id?: string
  message?: string
  status?: number
  email?: string
  name?: string
  token?: TokenModel
}

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (original)
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>  (edited)
 */
export interface IAuthControl {
  /**
   * Validate token.
   * @param token
   */
  checkToken (token: string): Promise<void>

  /**
   * Retrieves link for social auth redirect
   */
  getSocialAuthRedirectUri (social: string): Promise<SocialAuthRedirectUri>

  /**
   * Logs user to application.
   */
  login (payload: LoginFormPayload): Promise<TokenModel>

  /**
   * Logs user out of application.
   */
  logout (): Promise<void>

  /**
   * Send request to api for new reset link.
   *
   * @param payload
   */
  requestResetLink (payload: RequestResetLinkFormData): Promise<void>

  resetPassword (payload: RequestResetPasswordPayload): Promise<void>

  /**
   * Sign user up to application.
   */
  signup (payload: SignupFormPayload): Promise<TokenModel>

  /**
   * Logs in with social credentials.
   */
  setSocialCallback (params: SSOCallbackParams, provider: string, sessionId?: string | null, acceptContents?: boolean | null): Promise<SSOValidCallbackResponse | undefined> // TODO: Fix in Dummy also

  SSOPendingStatus (): boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface LoginFormPayload {
  /**
   * User's email.
   */
  email: string

  /**
   * User's password
   */
  password: string
}

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
export interface RequestResetPasswordPayload {
  /**
   * Email of the user.
   */
  email: string

  newPassword: string

  resetPasswordToken: string
}

/**
 * @author Katarzyna Otto <katarzyna.otto@movecloser.pl>
 */
export interface RequestResetLinkPayload {
  /**
   * Email of the user.
   */
  email: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface SignupFormPayload {
  /**
   * Accept privacy and policy check.
   */
  acceptPrivacy: boolean

  /**
   * Email of the user.
   */
  email: string

  /**
   * Name of the user.
   */
  firstName: string

  /**
   * Last name of the user.
   */
  lastName: string

  /**
   * Password of the user.
   */
  password: string

  /**
   * User phone number.
   */
  phone: string

  /**
   * Repeated password, for verifications.
   */
  passwordConfirmation: string

  /**
   * Subscribe on news check.
   */
  subscribeToNews: boolean
}
